import { useParentPosition } from "@/hooks/useParentPosition";
import { SM_UP_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";
import { hasHover, lastPressWasTouch } from "@/lib/utils/pointer";

import {
  IconChevronDown,
  IconSearch,
  IconSquare,
  IconSquareCheckFilled,
} from "@tabler/icons-react";
import { useTranslations, useLocale } from "next-intl";
import { useRef, useState, useEffect, useId, useMemo } from "react";
import { createPortal } from "react-dom";
import { sortOptionsForDisplay } from "@/lib/utils/sortOptionsForDisplay";

type MultiOptionInputProps = {
  /** DOM id on the focusable control div — lets hosts focus the picker by id
   *  (the condition-row keyboard chain: operator commit → value editor). */
  id?: string;
  border?: string;
  options: string[];
  defaultValues: string[];
  required: boolean;
  setParent: (values: string[]) => void;
  onFocusAction?: () => void;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  width?: string;
  disabled?: boolean;
  /** Optional per-option colors (option value -> hex) — renders a color dot
   *  in the dropdown rows. Used by colored MULTI_SELECT custom fields. */
  optionColors?: Record<string, string>;
  /** When true, display the dropdown rows in locale-aware alphabetical order.
   *  Opt-in: only user-defined SELECT-field option lists turn this on; other
   *  callers keep their deliberate order. */
  sortOptions?: boolean;
  /** An option that is mutually exclusive with all others (e.g. the filter
   *  "—" is-empty pick): selecting it clears every other selection, and
   *  selecting any other option clears it. */
  exclusiveOption?: string;
  /** Max number of options the guest may pick (MULTI_SELECT selection-count
   *  upper bound). When set, adding beyond it is blocked live. Undefined = no
   *  limit. `min` is enforced at submit time (can't live-block a shortfall). */
  maxSelections?: number;
  /** Options that are FULL (per-option capacity reached) and NOT already held by
   *  this guest — rendered greyed + non-selectable. An already-held full option
   *  is never in this set, so the guest can still deselect it. */
  disabledOptions?: string[];
  /** Text shown when nothing is selected. Pass "" to show nothing (filter row
   *  cells stay visually empty until a value is picked). */
  placeholder?: string;
  /** Render the dropdown into `document.body` instead of in place.
   *
   *  The list is `position: fixed` at VIEWPORT coordinates (`useParentPosition`).
   *  Any ancestor with a non-`none` transform/translate becomes the containing
   *  block for fixed descendants — so the list lands offset, and an
   *  `overflow-hidden` on that same ancestor then clips it away entirely. The
   *  seating guest panel is exactly that (`translate-x-0` + `overflow-hidden`),
   *  which is why it already portals its own display-fields popup.
   *
   *  Off by default: every existing call site keeps its current DOM. */
  portal?: boolean;
};

export const MultiOptionInput: React.FC<MultiOptionInputProps> = ({
  id,
  border = "outline-tertiary border focus:focus-primary",
  options,
  defaultValues,
  required,
  setParent,
  onFocusAction,
  height = "h-9",
  surface = "surface-primary",
  fontSize = "body-2-regular",
  padding = "px-2 py-1",
  rounded = "rounded-lg",
  width = "w-full",
  disabled,
  optionColors,
  sortOptions = false,
  exclusiveOption,
  maxSelections,
  disabledOptions,
  placeholder,
  portal = false,
}) => {
  const disabledSet = useMemo(
    () => new Set(disabledOptions ?? []),
    [disabledOptions],
  );
  const t = useTranslations("inputColumnComponent");
  const locale = useLocale();
  const isMouseDownOnDropdown = useRef(false);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(defaultValues);
  // Type-to-filter the dropdown rows. Auto-enabled only for long option lists
  // (e.g. the 100+ language list) so short multi-selects are visually unchanged.
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number>(0);
  const [positionReady, setPositionReady] = useState(false);

  useEffect(() => {
    setSelectedValues(defaultValues);
  }, [defaultValues]);

  // Clear the search filter whenever the dropdown closes so it reopens clean.
  useEffect(() => {
    if (!showOptions) setQuery("");
  }, [showOptions]);

  const { dropdownPosition, setDropdownPosition } = useParentPosition();
  const divRef = useRef<HTMLDivElement>(null);
  /** Stable id for aria-controls — the listbox is a sibling in a fixed
   *  overlay, so it cannot be found structurally. */
  const listboxId = `${useId()}-listbox`;

  /**
   * Same move as the single-select twin: on a phone the list docks to the
   * bottom of the screen instead of being pinned to the trigger. Anchoring over
   * the iOS keyboard needed three separate fixes and still landed wrong; a
   * bottom sheet has no anchor to get wrong. `bandInset` keeps it resting ON
   * the keyboard rather than behind it, for the case where some other field
   * still holds focus.
   */
  const isSmUp = useMediaQuery(SM_UP_QUERY);
  const sheetMode = !isSmUp;

  // Capture div width when dropdown opens
  useEffect(() => {
    if (divRef.current && showOptions) {
      setDropdownWidth(divRef.current.offsetWidth);
    }
  }, [showOptions]);

  // Wait for position to be calculated before marking ready
  useEffect(() => {
    if (showOptions && dropdownPosition.top !== 0) {
      setPositionReady(true);
    }
  }, [showOptions, dropdownPosition]);

  // Update position on scroll/resize while dropdown is open
  useEffect(() => {
    if (showOptions && divRef.current) {
      const updatePosition = () => {
        if (divRef.current) {
          setDropdownPosition(divRef);
        }
      };

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      // iOS raises the software keyboard without firing a window resize — only
      // the visual viewport changes, and the list would keep its stale anchor.
      window.visualViewport?.addEventListener("resize", updatePosition);
      window.visualViewport?.addEventListener("scroll", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
        window.visualViewport?.removeEventListener("resize", updatePosition);
        window.visualViewport?.removeEventListener("scroll", updatePosition);
      };
    }
  }, [showOptions]);



  // Update position on scroll/resize while dropdown is open
  useEffect(() => {
    if (showOptions && divRef.current) {
      const updatePosition = () => {
        if (divRef.current) {
          setDropdownPosition(divRef);
        }
      };

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      // iOS raises the software keyboard without firing a window resize — only
      // the visual viewport changes, and the list would keep its stale anchor.
      window.visualViewport?.addEventListener("resize", updatePosition);
      window.visualViewport?.addEventListener("scroll", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
        window.visualViewport?.removeEventListener("resize", updatePosition);
        window.visualViewport?.removeEventListener("scroll", updatePosition);
      };
    }
  }, [showOptions]);


  // Initialize with default values or first option if required and empty
  useEffect(() => {
    if (required && selectedValues?.length === 0 && options?.length > 0) {
      const initialValues = [options[0]];
      setSelectedValues(initialValues);
      setParent(initialValues);
    }
  }, [required, options, setParent]);

  const handleSelection = (value: string) => {
    // Prevent selection when viewOnly is true
    if (disabled) return;
    // A full (capacity-reached) option can't be newly picked; deselecting an
    // already-held one is still allowed.
    if (disabledSet.has(value) && !selectedValues.includes(value)) return;

    let newSelectedValues;

    if (exclusiveOption && value === exclusiveOption) {
      // The exclusive option (e.g. filter "—" is-empty): selecting it clears
      // everything else; clicking it again leaves it (empty == empty).
      newSelectedValues = selectedValues.includes(value) ? [value] : [value];
    } else if (selectedValues.includes(value)) {
      // Remove value if already selected (and drop any exclusive sentinel).
      newSelectedValues = selectedValues.filter(
        (item) => item !== value && item !== exclusiveOption,
      );
    } else {
      // Adding a new value. Enforce the max-selections cap live: once the guest
      // holds `maxSelections` options, further adds are ignored (the row stays
      // pickable-off so they can swap). The exclusive sentinel never counts.
      const activeCount = selectedValues.filter(
        (item) => item !== exclusiveOption,
      ).length;
      if (maxSelections != null && activeCount >= maxSelections) {
        return;
      }
      // Add value, dropping the exclusive sentinel if it was set.
      newSelectedValues = [
        ...selectedValues.filter((item) => item !== exclusiveOption),
        value,
      ];
    }

    // If required and attempting to clear all selections, keep at least one
    if (required && newSelectedValues?.length === 0 && options?.length > 0) {
      newSelectedValues = [options[0]];
    }

    setSelectedValues(newSelectedValues);
    setParent(newSelectedValues);
  };

  const handleToggleDropdown = () => {
    // Prevent toggling when viewOnly is true
    if (disabled) return;

    if (!showOptions) {
      if (onFocusAction) onFocusAction();
      setDropdownPosition(divRef);
      setPositionReady(false);
    } else {
      setPositionReady(false);
    }
    setShowOptions(!showOptions);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent keyboard interactions when viewOnly is true
    if (disabled) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggleDropdown();
    } else if (e.key === "Escape") {
      // Escape with the dropdown OPEN closes only the dropdown — it must not
      // bubble into an enclosing form/modal cancel handler and discard the
      // user's edits (OptionInput's rule, which this field never had: inside
      // the detail popups it closed the whole editor and lost everything).
      // Closed field: let it bubble — that is the table cell's edit exit.
      if (!showOptions) return;
      e.stopPropagation();
      setShowOptions(false);
      setPositionReady(false);
    }
  };

  const handleFocus = () => {
    // Prevent focus action when viewOnly is true
    if (disabled) return;

    // Calculate dropdown position for keyboard navigation
    setDropdownPosition(divRef);
    if (onFocusAction) onFocusAction();
  };

  // Alphabetical display order for the dropdown only, opt-in via `sortOptions`.
  // `options` stays canonical so the required-default and "can't clear the last
  // one" rules keep picking options[0] (the field's first option), not the
  // alphabetically-first one.
  const sortedOptions = useMemo(
    () =>
      sortOptions ? sortOptionsForDisplay(options, undefined, locale) : options,
    [sortOptions, options, locale],
  );

  // Show a search box only for long lists (the language filter has 100+).
  const showSearch = options.length > 8;
  const visibleOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? sortedOptions.filter((o) => o.toLowerCase().includes(q))
      : sortedOptions;
  }, [sortedOptions, query]);

  // Focus the search box when the dropdown opens so the user can type immediately.
  useEffect(() => {
    // Not after a tap: iOS would raise the keyboard over the list the user
    // just opened. Same gate as PView/PFilter.
    if (showOptions && positionReady && showSearch && !lastPressWasTouch()) {
      // preventScroll: focusing must not scroll the surrounding table/page when
      // the dropdown opens from a cell (the dropdown is fixed-positioned anyway).
      searchInputRef.current?.focus({ preventScroll: true });
    }
  }, [showOptions, positionReady, showSearch]);

  // Opt-in escape hatch from a transformed/clipping ancestor — see the `portal`
  // prop. Only ever invoked while the dropdown is open, i.e. after hydration.
  const withPortal = (node: React.ReactNode) =>
    portal ? createPortal(node, document.body) : node;

  return (
    <>
      <div
        id={id}
        ref={divRef}
        // Combobox semantics — and `aria-expanded` is load-bearing beyond a11y:
        // host dialogs with a window-level Escape (HubDialogShell) probe for it
        // to know an inner overlay owns the key.
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={showOptions}
        // Only while OPEN, and on the trigger as well as the overlay below: the
        // listbox is a fixed-position SIBLING, so a host probing from the
        // focused element finds nothing here otherwise.
        data-escape-owner={showOptions ? "true" : undefined}
        // Only while the listbox exists — a dangling IDREF is an a11y error.
        aria-controls={showOptions ? listboxId : undefined}
        aria-disabled={disabled || undefined}
        onClick={handleToggleDropdown}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onMouseEnter={() => !disabled && hasHover() && setDropdownPosition(divRef)}
        tabIndex={disabled ? -1 : 0} // Remove from tab order when viewOnly
        className={`${padding} ${surface} ${rounded} ${height} ${width} flex items-center justify-between ${fontSize} ${border} focus:outline-hidden ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        } outline-quaternary`}
      >
        <div className="flex-1 min-w-0 overflow-hidden">
          {selectedValues?.length > 0 ? (
            // Single joined line so several long selections (e.g. timestamped
            // import batches) collapse to one clean ellipsized string instead of
            // a row of individually-truncated fragments.
            <p className="truncate w-full">{selectedValues.join(", ")}</p>
          ) : (
            <span className="font-quaternary">
              {placeholder ?? t("selectOption")}
            </span>
          )}
        </div>
        <IconChevronDown
          size={16}
          className={`ml-2 shrink-0 transition-transform duration-200 ${
            showOptions ? "rotate-180" : ""
          }`}
        />
      </div>
      {showOptions && !disabled && positionReady && withPortal(
        <div
          // Ancestor of the search box, and the only node a `closest()` probe
          // from inside the dropdown can reach — the trigger carrying
          // `aria-expanded` is a SIBLING. Host dialogs use this to leave the
          // key to us (see HubDialogShell.OVERLAY_OPEN).
          data-escape-owner="true"
          // A positioning hull only. The dismissing click used to live here,
          // which made this element BOTH the catcher and the list's parent —
          // and `touch-action` intersects down the hit-test chain, so blocking
          // the background scroll here would have taken the option list's own
          // scrolling with it. The catcher is now a sibling of the list, below.
          // z: `portal` exists to escape a transformed/clipping ancestor, and that
          // ancestor is typically also a z-50 panel or modal — portaling to body
          // puts this list in the ROOT stacking context, where z-20 would paint
          // BEHIND the very host it just escaped. z-1000 is the house rung for a
          // helper picker opened from a modal (ImagePicker, see
          // ModalWrapper.Z_INDEX_MAP): above the base/host/nested modal band,
          // below `critical` and the AI overlay. Non-portaled callers keep z-20 —
          // they are inside their host's own stacking context, where it is right.
          className={`fixed ${portal ? "z-[1000]" : "z-20"} ${
            sheetMode
              ? // The geometry glides: iOS reports the shrinking viewport in
                // steps while the keyboard slides in, and each step moves this
                // box. Animated rather than jumping.
                "inset-x-0 flex items-end bg-black/50 transition-[top,height] duration-200 ease-out motion-reduce:transition-none"
              : "inset-0 h-full w-full"
          }`}
          // In sheet mode the hull covers exactly the visible strip, given both
          // edges. Its child then docks to the bottom of THAT, so the keyboard
          // never gets a chance to sit on top of the list.
          style={
            sheetMode
              ? {
                  top: `${dropdownPosition.bandTop}px`,
                  height: `${dropdownPosition.bandHeight}px`,
                }
              : undefined
          }
        >
          <ul
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            className={`space-y-1 z-30 surface-secondary shadow-lg overflow-y-auto overscroll-contain ${
              sheetMode
                ? "w-full p-2 rounded-t-3xl border-t outline-tertiary"
                : "fixed mt-1 p-1 border outline-tertiary rounded-lg"
            }`}
            style={
              sheetMode
                ? {
                    // A percentage of the hull, which is the visible strip.
                    // Not `dvh`: that unit is the viewport with the browser's
                    // toolbars collapsed and overflows whenever one is out.
                    maxHeight: "70%",
                  }
                : {
                    ...(dropdownPosition.showAbove
                      ? {
                          bottom: `${dropdownPosition.top}px`,
                          left: `${dropdownPosition.left}px`,
                        }
                      : {
                          top: `${dropdownPosition.top}px`,
                          left: `${dropdownPosition.left}px`,
                        }),
                    minWidth: `${dropdownWidth}px`,
                    width: "max-content",
                    maxWidth: `min(20rem, calc(100vw - ${dropdownPosition.left}px - 16px))`,
                    maxHeight: `${dropdownPosition.maxHeight}px`,
                  }
            }
            // pointerdown fires with touchstart — BEFORE the shield below sees
            // a click — whereas the synthesized mousedown arrives later. Without
            // it a finger could close the list instead of picking from it.
            onPointerDown={() => {
              isMouseDownOnDropdown.current = true;
            }}
            onMouseDown={() => {
              isMouseDownOnDropdown.current = true;
            }}
            onMouseUp={() => {
              isMouseDownOnDropdown.current = false;
            }}
          >
            {showSearch && (
              // Not an option — a listbox may only contain options, so the
              // filter row opts out of the role hierarchy.
              <li
                role="presentation"
                className="sticky top-0 z-10 -m-1 mb-1 p-1 surface-secondary"
              >
                <div className="flex items-center gap-1.5 px-2 surface-primary border outline-tertiary rounded-lg">
                  <IconSearch size={14} className="shrink-0 font-quaternary" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        // Same containment as the trigger: dismiss the
                        // dropdown, never the surrounding form.
                        e.stopPropagation();
                        setShowOptions(false);
                        setPositionReady(false);
                      }
                    }}
                    placeholder={t("search")}
                    data-search-input
                    className="w-full bg-transparent py-1.5 body-3-regular outline-hidden"
                  />
                </div>
              </li>
            )}
            {visibleOptions?.length > 0 ? (
              <>
                {visibleOptions.map((option, index) => {
                  const isFull =
                    disabledSet.has(option) && !selectedValues.includes(option);
                  return (
                  <li
                    key={`option-${index}`}
                    role="option"
                    aria-selected={selectedValues.includes(option)}
                    aria-disabled={isFull || undefined}
                    className={`py-1 rounded-lg body-3-regular px-2 flex items-center justify-between gap-2 ${
                      isFull
                        ? "opacity-40 cursor-not-allowed font-quaternary"
                        : selectedValues.includes(option)
                          ? "surface-tertiary font-primary cursor-pointer hover:surface-tertiary"
                          : "font-quaternary cursor-pointer hover:surface-tertiary hover:font-primary"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelection(option);
                    }}
                  >
                    {optionColors?.[option] && (
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full border outline-primary"
                        style={{ backgroundColor: optionColors[option] }}
                      />
                    )}
                    <span className="truncate flex-1 min-w-0">{option}</span>
                    {isFull ? (
                      <span className="shrink-0 body-3-regular font-quaternary">
                        {t("optionFull")}
                      </span>
                    ) : selectedValues.includes(option) ? (
                      <IconSquareCheckFilled
                        size={14}
                        className="shrink-0"
                      />
                    ) : (
                      <IconSquare size={14} className="shrink-0" />
                    )}
                  </li>
                  );
                })}
              </>
            ) : (
              <li
                role="presentation"
                className="py-8 text-center font-quaternary body-3-regular"
              >
                {t("noFieldsFound")}
              </li>
            )}
          </ul>
          {sheetMode && (
            // The list's colour, continued below the strip we believe is
            // visible — absolutely positioned and outside the list, so it adds
            // no layout. Covers a misjudged keyboard edge instead of leaving a
            // band of undimmed page; falls off the screen when there is none.
            <div
              aria-hidden
              className="absolute inset-x-0 top-full h-80 surface-secondary"
            />
          )}
          {/* Outside-click catcher. `touch-none` so a swipe anywhere outside
              the list scrolls NOTHING: this sheet covers the screen while the
              list is open and is not a scroller itself, so the gesture used to
              pass through to the page behind — which slid away underneath a
              `fixed` dropdown that stayed exactly where it was. Touch only, so
              a desktop wheel is unaffected. */}
          <div
            className="fixed inset-0 h-full w-full touch-none"
            onClick={(e) => {
              e.stopPropagation();
              // Read AND reset: the pointerdown guard has no other consumer
              // here (unlike OptionInput, which clears it in onBlur). Left
              // standing after a press whose mouse events were suppressed, it
              // would swallow every later outside tap and the list could not be
              // dismissed.
              const guarded = isMouseDownOnDropdown.current;
              isMouseDownOnDropdown.current = false;
              if (!guarded) {
                setShowOptions(false);
                setPositionReady(false);
              }
            }}
          />
        </div>
      )}
    </>
  );
};
